<template>
  <div
    class="bg-white dark:bg-gray-800 rounded-md border border-gray-200 dark:border-gray-700 hover:border-teal-300 dark:hover:border-teal-700 transition-all duration-200 px-3 py-2.5 sm:px-4 sm:py-3"
    :class="{
      'opacity-60': isPerformed,
      'ring-2 ring-teal-500 ring-offset-2 border-teal-500': hasVoted && !isPerformed,
      'bg-teal-50/50 dark:bg-teal-900/10': hasVoted && !isPerformed,
      'shadow-sm hover:shadow-md': !isPerformed
    }"
  >
    <div class="songcard flex items-center justify-between gap-3">
      <!-- Left: title / author / categories / state badges -->
      <div class="flex-1 min-w-0">
        <div class="flex items-center gap-1.5">
          <h3 class="text-sm font-semibold text-gray-900 dark:text-white truncate">
            {{ song.title }}
          </h3>
          <i
            v-if="isPerformed"
            class="pi pi-check-circle text-teal-600 dark:text-teal-400 text-xs"
            title="Performed"
          ></i>
        </div>
        <p v-if="song.author" class="text-gray-600 dark:text-gray-400 text-xs truncate">
          {{ song.author }}
        </p>
        <div class="flex items-center gap-1.5 mt-0.5 flex-wrap">
          <span
            v-for="category in categoryList"
            :key="category"
            class="inline-block px-1.5 py-0.5 text-[11px] bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded"
          >
            {{ category }}
          </span>
          <span
            v-if="song.year"
            class="inline-block px-1.5 py-0.5 text-[11px] bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-200 rounded"
          >
            {{ song.year }}
          </span>
          <span
            v-else-if="isPerformed"
            class="inline-block px-1.5 py-0.5 text-[11px] bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 rounded font-semibold"
          >
            Performed
          </span>
        </div>
      </div>

      <!-- Right: compact vote count + actions -->
      <div class="flex items-center gap-2 shrink-0">
        <div v-if="showVoteCount" class="text-right leading-tight">
          <div class="text-[10px] uppercase tracking-wide text-gray-400 dark:text-gray-500">
            Votes
          </div>
          <div class="text-sm font-semibold text-gray-900 dark:text-white">
            {{ voteCount }}
          </div>
        </div>

        <div v-if="showVoteButton" class="flex flex-col gap-2">
          <button
            v-if="votingEnabled && !hasVoted && !isPerformed"
            :disabled="!votingEnabled"
            class="px-3 py-2 bg-teal-600 text-white rounded-md hover:bg-teal-700 disabled:bg-gray-300 disabled:text-gray-500 disabled:cursor-not-allowed transition-colors duration-200 text-xs font-medium whitespace-nowrap"
            @click="handleVote"
          >
            {{ queueActionLabel || "Vote" }}
          </button>

          <button
            v-else-if="hasVoted"
            disabled
            class="px-3 py-2 bg-teal-100 dark:bg-teal-900/30 text-teal-700 dark:text-teal-300 rounded-md text-xs font-medium opacity-70 cursor-not-allowed"
          >
            Voted
          </button>

          <button
            v-else-if="isPerformed"
            disabled
            class="px-3 py-2 bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400 rounded-md text-xs font-medium opacity-70 cursor-not-allowed"
          >
            Performed
          </button>

          <button
            v-if="showMarkPerformed && authStore.isAdmin && !isPerformed"
            class="px-3 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors text-xs font-medium"
            @click="handleMarkPerformed"
          >
            Mark Performed
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { computed } from "vue";
import { useAuthStore } from "../../stores/auth";

export default {
  name: "SongCard",
  props: {
    song: {
      type: Object,
      required: true
    },
    eventId: {
      type: String,
      required: true
    },
    voteCount: {
      type: Number,
      default: 0
    },
    hasVoted: {
      type: Boolean,
      default: false
    },
    isPerformed: {
      type: Boolean,
      default: false
    },
    votingEnabled: {
      type: Boolean,
      default: true
    },
    showMarkPerformed: {
      type: Boolean,
      default: true
    },
    showVoteCount: {
      type: Boolean,
      default: true
    },
    showVoteButton: {
      type: Boolean,
      default: true
    },
    queueActionLabel: {
      type: String,
      default: ""
    }
  },
  emits: ["vote", "mark-performed"],
  setup(props, { emit }) {
    const authStore = useAuthStore();
    const categoryList = computed(() => {
      if (!props.song) return [];
      if (Array.isArray(props.song.categories) && props.song.categories.length > 0) {
        return props.song.categories;
      }
      if (props.song.category) {
        return [props.song.category];
      }
      return [];
    });

    const handleVote = () => {
      emit("vote", props.song.id);
    };

    const handleMarkPerformed = () => {
      emit("mark-performed", props.song.id);
    };

    return {
      authStore,
      categoryList,
      handleVote,
      handleMarkPerformed
    };
  }
};
</script>
